import { createContext, ReactNode, useContext, useState, useMemo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMedia } from 'react-use';
import { Modal, ModalContent, ModalTitleInfo, Spinner } from '@c2fo/liquidity';
import {
  MoneyBillWaveDuotoneIcon,
  ChartLineIcon,
  UsersDuotoneIcon,
  BuildingColumnsIconDuotone,
  UserGearDuotoneIcon,
  FileInvoiceDuotoneIcon,
  HouseChimneyDuotoneIcon,
  RabbitRunningDuotoneIcon,
} from '@c2fo/liquidity/icons';
import BuyerLogo from '@/enterprise/components/BuyerLogo';
import useInstantPayEnabled from '@/enterprise/components/InstantPay/useInstantPayEnabled';
import useGroupByBuyerCurrency from '@/enterprise/components/InvoiceCentral/useGroupByBuyerCurrency';
import { formatBuyerName } from '@/enterprise/components/InvoiceCentral/utils';
import useEnableAutomations from '@/enterprise/components/useEnableAutomations';
import useEnableUnification from '@/enterprise/components/useEnableUnification';
import { hasActiveInstantPayDivision } from '@/enterprise/data/instantPayEnrollment';
import { NAVIGATION_CONFIG_KEY } from '@/enterprise/data/navigationArtifactSelectors';
import { useNavigationArtifact } from '@/enterprise/data/useArtifacts';
import { useFinanceIqEnabled } from '@/enterprise/data/useFinanceIqEnabled';
import useTakerMarkets from '@/enterprise/data/useTakerMarkets';
import { ENF_VERSION, useEnfVersion } from '@/enterprise/features/extendedNetworkFinance/useEnfVersion';
import { useIsLendingEligible } from '@/enterprise/features/lendingConnections/utils';
import useFeature from '@/lib/features';
import { asEnterpriseLink } from '@/utils/experienceLinkHelper';
import useLocaleFormat from '@/utils/useLocaleFormat';
import { useReadOnlyUser } from '@/utils/useRestrictions';
import { NavigationItem } from './core/Navigation';
import { EXTENDED_NETWORK_FINANCE_NAVIGATION_CONFIG, FUNDBOX_NAVIGATION_CONFIG } from './navigationConfigRegistry';

type ExternalRedirect = Record<
  'FINANCE_IQ',
  {
    label: string;
    url: string;
  }
>;

interface NavigationProviderProps {
  children: ReactNode;
}

interface NavigationContextType {
  isSubNavOpen: boolean;
  setIsSubNavOpen: (open: boolean) => void;
  selectedPrimaryNavItem: NavigationItem | null;
  setSelectedPrimaryNavItem: (item: NavigationItem | null) => void;
  isNavCollapsed: boolean;
  setIsNavCollapsed: (collapsed: boolean) => void;
  closeSubNav: () => void;
  isNavCollapseAllowed: boolean;
  isMobile: boolean;
  initiateExternalRedirect: (redirectKey: string) => void;
  isMobileNavOpen: boolean;
  setIsMobileNavOpen: (open: boolean) => void;
  closeMobileNav: () => void;
  activeItemId: string | undefined;
  setActiveItemId: (id: string | undefined) => void;
}

const NavigationContext = createContext<NavigationContextType | undefined>(undefined);

export const useNavigationContext = () => {
  const context = useContext(NavigationContext);

  if (!context) {
    throw new Error('useNavigationContext must be used within a NavigationProvider');
  }

  return context;
};

const EMPTY_NAVIGATION_ITEMS: NavigationItem[] = [];

const useInvoiceCentralNavigationItem = () => {
  const { t } = useTranslation();
  const { data: takerMarkets } = useTakerMarkets();
  const buyers = useGroupByBuyerCurrency({ takerMarkets, includeAwardedInvoices: false });
  const { asCurrency } = useLocaleFormat();

  return useMemo(() => {
    if (!buyers || buyers.length === 0) {
      return EMPTY_NAVIGATION_ITEMS;
    }

    const subItems = buyers.map((buyer) => {
      return {
        id: `invoice-central-${buyer.makerOrganizationUuid}-${buyer.currency}`,
        label: (
          <div className="flex gap-2 overflow-hidden">
            <BuyerLogo buyerLogoUrl={buyer.makerOrganizationLogo} buyerName={buyer.makerOrganizationName} />
            <div className="flex flex-col">
              <div>{formatBuyerName({ buyerToFormat: buyer, buyers })}</div>
              <div className="text-xs text-text-secondary">
                {/* includeAwardedInvoices false in useGroupByBuyerCurrency handles removing awarded invoices from the total */}
                {asCurrency(buyer.stats.totalInvoiceAmount, buyer.currency)}
              </div>
            </div>
          </div>
        ),
        path: asEnterpriseLink(`invoice-central/${buyer.makerOrganizationUuid}/${buyer.currency}/eligible`),
        breadcrumbLabel: formatBuyerName({ buyerToFormat: buyer, buyers }),
      };
    });

    return [
      {
        id: 'invoice-central',
        label: t('invoiceCentral.title'),
        Icon: FileInvoiceDuotoneIcon,
        subItems,
      },
    ];
  }, [buyers, t, asCurrency]);
};

const useExtendedNetworkFinanceNavigationItem = () => {
  const enfVersion = useEnfVersion();
  const { data: artifact } = useNavigationArtifact(NAVIGATION_CONFIG_KEY.EXTENDED_NETWORK_FINANCE);

  return useMemo(() => {
    if (enfVersion === ENF_VERSION.OFF || !artifact) {
      return EMPTY_NAVIGATION_ITEMS;
    }

    /**
     * The artifact only says this user qualifies for `EXTENDED_NETWORK_FINANCE`. The treatment picks
     * the shape, and the payload's text, icon, target, and children are ignored.
     */
    return [
      enfVersion === ENF_VERSION.V0_1
        ? EXTENDED_NETWORK_FINANCE_NAVIGATION_CONFIG.nested
        : EXTENDED_NETWORK_FINANCE_NAVIGATION_CONFIG.leaf,
    ];
  }, [enfVersion, artifact]);
};

export const NavigationProvider = ({ children }: NavigationProviderProps) => {
  const { t } = useTranslation();
  const [isNavCollapsed, setIsNavCollapsed] = useState(false);
  const [isSubNavOpen, setIsSubNavOpen] = useState(false);
  const [selectedPrimaryNavItem, setSelectedPrimaryNavItem] = useState<NavigationItem | null>(null);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [activeItemId, setActiveItemId] = useState<string | undefined>(undefined);
  const [externalRedirectInProgress, setExternalRedirectInProgress] = useState<{
    label: string;
    url: string;
  } | null>(null);
  const isMobile = useMedia('(max-width: 768px)');
  const isWide = useMedia('(min-width: 1556px)');
  const appDomain = (import.meta.env.VITE_LOGIN_URL ?? '').replace('/login', '');
  const [isNavCollapseAllowed, setIsNavCollapseAllowed] = useState(() => !isWide && !isMobile);

  const closeSubNav = useCallback(() => {
    setIsSubNavOpen(false);
    setSelectedPrimaryNavItem(null);
  }, []);

  const closeMobileNav = useCallback(() => {
    setIsMobileNavOpen(false);
    document.body.style.overflow = '';
  }, []);

  const initiateExternalRedirect = useCallback(
    (redirectKey: string) => {
      const externalRedirectConfig: ExternalRedirect = {
        FINANCE_IQ: {
          label: 'FinanceIQ',
          url: `${appDomain}/financeiq/?utm_partner=&utm_lang=en_US&utm_source=c2fo&utm_medium=in_app&utm_region=amer&utm_lifecycle=cross&utm_audience=na&utm_campaign=financeiq&utm_series=ent-top-nav426&utm_country=us`,
        },
      };

      const config = externalRedirectConfig[redirectKey as keyof ExternalRedirect];
      if (!config) return;

      const { label, url } = config;

      setExternalRedirectInProgress({
        label,
        url,
      });

      setTimeout(() => {
        setExternalRedirectInProgress(null);
        window.location.href = url;
      }, 1500);
    },
    [appDomain]
  );

  useEffect(() => {
    if (isNavCollapsed) {
      setIsSubNavOpen(false);
    }
  }, [isNavCollapsed]);

  /**
   * handle auto collapse of the nav when we shift in and out of mobile and wide mode.
   */
  useEffect(() => {
    if (isWide || isMobile) {
      setIsNavCollapsed(false);
    }

    // only allow collapse if screen is not wide enough to fit the entire expanded side nav + max width content
    setIsNavCollapseAllowed(!isWide && !isMobile);
  }, [isWide, isMobile]);

  const contextValue: NavigationContextType = useMemo(
    () => ({
      isSubNavOpen,
      setIsSubNavOpen,
      selectedPrimaryNavItem,
      setSelectedPrimaryNavItem,
      isNavCollapsed,
      setIsNavCollapsed,
      closeSubNav,
      isNavCollapseAllowed,
      isMobile,
      initiateExternalRedirect,
      isMobileNavOpen,
      setIsMobileNavOpen,
      closeMobileNav,
      activeItemId,
      setActiveItemId,
    }),
    [
      isSubNavOpen,
      selectedPrimaryNavItem,
      isNavCollapsed,
      closeSubNav,
      isNavCollapseAllowed,
      isMobile,
      initiateExternalRedirect,
      isMobileNavOpen,
      closeMobileNav,
      activeItemId,
    ]
  );

  return (
    <NavigationContext.Provider value={contextValue}>
      {children}
      <Modal open={!!externalRedirectInProgress}>
        <ModalTitleInfo>{t('navigation.externalRedirect.title')}</ModalTitleInfo>
        <ModalContent center>
          <p>{t('navigation.externalRedirect.description')}</p>
          <p className="text-lg font-bold">{externalRedirectInProgress?.label}</p>
          <Spinner className="h-12 w-12 fill-primary-500" />
        </ModalContent>
      </Modal>
    </NavigationContext.Provider>
  );
};

export const useNavigationItems = (): NavigationItem[] => {
  const { t } = useTranslation();
  const readOnlyUser = useReadOnlyUser();
  const enableFinanceIQ = useFinanceIqEnabled();
  const isLendingEligible = useIsLendingEligible();
  const [fundboxEnabled] = useFeature('enterprise-ui_enableFundbox');
  const { data: fundboxNavigationArtifact } = useNavigationArtifact(NAVIGATION_CONFIG_KEY.FUNDBOX);
  const enableUnification = useEnableUnification();
  const enableAutomations = useEnableAutomations();
  const instantPayOnboardingEnabled = useInstantPayEnabled();
  const { data: takerMarkets } = useTakerMarkets();
  const hasActiveInstantPay = hasActiveInstantPayDivision(takerMarkets);
  const invoiceCentralNavigationItem = useInvoiceCentralNavigationItem();
  const extendedNetworkFinanceNavigationItem = useExtendedNetworkFinanceNavigationItem();

  return useMemo(() => {
    const financeIQNavigationItem = enableFinanceIQ
      ? [
          {
            id: 'divider-1',
            label: '',
            isDivider: true,
          },
          {
            id: 'financeiq',
            label: 'FinanceIQ',
            Icon: ChartLineIcon,
            externalRedirectKey: 'FINANCE_IQ',
            external: true,
          },
        ]
      : [];

    const lendingNavigationItem = isLendingEligible
      ? [
          {
            id: 'lending',
            label: t('core.lending'),
            Icon: BuildingColumnsIconDuotone,
            path: asEnterpriseLink('lending'),
          },
        ]
      : [];

    // The artifact qualifies the user for `FUNDBOX`; the row itself is written in the registry.
    const fundboxNavigationItem = fundboxEnabled && fundboxNavigationArtifact ? [FUNDBOX_NAVIGATION_CONFIG] : [];

    const homepageItem = enableUnification
      ? [
          {
            id: 'homepage',
            label: t('navigation.homepage'),
            Icon: HouseChimneyDuotoneIcon,
            path: asEnterpriseLink('homepage'),
          },
        ]
      : [];

    const offerActivityItem = enableUnification
      ? [
          {
            id: 'offer-activity',
            label: t('core.offerActivity'),
            path: asEnterpriseLink('offer-activity'),
          },
        ]
      : [
          {
            id: 'markets',
            label: t('core.markets'),
            path: asEnterpriseLink('customers'),
            intercomTarget: 'sideNavMarkets',
          },
        ];

    return [
      ...homepageItem,
      {
        id: 'early-pay',
        label: t('core.earlyPay'),
        Icon: MoneyBillWaveDuotoneIcon,
        intercomTarget: 'sideNavEarlyPay',
        subItems: [
          ...offerActivityItem,
          {
            id: 'build-offer',
            disabled: readOnlyUser,
            label: t('core.buildOffer'),
            path: asEnterpriseLink('build-offer'),
            intercomTarget: 'sideNavBuildOffer' as const,
          },
          {
            id: 'history',
            label: enableUnification ? t('core.earlyPayHistory') : t('taker.consolidatedHistory.general'),
            breadcrumbLabel: enableUnification ? t('core.history') : undefined,
            path: asEnterpriseLink('history'),
            intercomTarget: 'sideNavConsolidatedHistory',
          },
          ...(enableAutomations
            ? [
                {
                  id: 'automation',
                  label: t('automation.automation'),
                  path: asEnterpriseLink('automation'),
                  intercomTarget: 'sideNavAutomation',
                },
              ]
            : []),
        ],
      },
      ...invoiceCentralNavigationItem,
      {
        id: 'find-customers',
        label: t('findYourCustomers.title'),
        Icon: UsersDuotoneIcon,
        path: asEnterpriseLink('find-your-customers'),
        intercomTarget: 'sideNavFindYourCustomers',
      },
      ...(instantPayOnboardingEnabled
        ? [
            {
              id: 'instant-pay',
              label: t('core.instantPay'),
              Icon: RabbitRunningDuotoneIcon,
              subItems: [
                {
                  id: 'instant-pay-benefits-and-impact',
                  label: t('instantPay.benefitsAndImpact'),
                  path: asEnterpriseLink('instant-pay/benefits-and-impact'),
                },
                {
                  id: 'instant-pay-how-it-works',
                  label: t('instantPay.onboarding.howItWorks.title'),
                  path: asEnterpriseLink('instant-pay/how-it-works'),
                },
                {
                  id: 'instant-pay-activation',
                  label: t('instantPay.activation'),
                  path: asEnterpriseLink('instant-pay/activation'),
                },
                ...(hasActiveInstantPay
                  ? [
                      {
                        id: 'instant-pay-substitution-records',
                        label: t('instantPay.substitutions.title'),
                        path: asEnterpriseLink('instant-pay/substitution-records'),
                      },
                    ]
                  : []),
              ],
            },
          ]
        : []),
      ...lendingNavigationItem,
      ...fundboxNavigationItem,
      ...extendedNetworkFinanceNavigationItem,
      ...financeIQNavigationItem,
      {
        id: 'account-settings',
        label: t('core.accountSettings'),
        Icon: UserGearDuotoneIcon,
        path: asEnterpriseLink('account/settings'),
        position: 'bottom',
        intercomTarget: 'sideNavAccountSettings',
      },
    ];
  }, [
    t,
    enableFinanceIQ,
    enableUnification,
    extendedNetworkFinanceNavigationItem,
    fundboxEnabled,
    fundboxNavigationArtifact,
    instantPayOnboardingEnabled,
    hasActiveInstantPay,
    isLendingEligible,
    readOnlyUser,
    invoiceCentralNavigationItem,
    enableAutomations,
  ]);
};
